// The Commercial Pilot Aptitude Diagnostic (plan.txt sections 71-75, 113):
// a single fixed-order battery establishing a broad baseline across 7
// ability domains. Deliberately not every commercial module (section 71:
// "Do NOT include every module") and explicitly excludes Physics (section
// 72: "learned knowledge", not aptitude). Mirrors the shape of
// data/mockTestProfiles.js's battery definitions (moduleId +
// administrationMinutes + optional breakAfter) but is its own file/system
// (see commercialDiagnosticRun.js) rather than a MOCK_TEST_PROFILES entry,
// since that system's service-profile scoping and paid credit-gating are
// specific to the military "Full Mock Test" product and don't apply here
// (section 94 lists "limited diagnostic" as a potential FREE feature).
import { MODULE_BY_ID } from '../constants/modules';

export const DIAGNOSTIC_DOMAINS = [
  'Numerical',
  'Perceptual Speed',
  'Spatial',
  'Working Memory',
  'Reasoning',
  'Psychomotor',
  'Multitasking'
];

// administrationMinutes mirrors each module's own standard Assessment-mode
// medium-tier duration (utils/storage.js's DEFAULT_SETTINGS) -- the
// diagnostic does not override per-module timing, it just runs each
// section's already-standardized assessment back to back. Total: 38
// minutes, within section 71's ~35-45 minute target.
const SECTIONS = [
  ['numerical-reasoning', 8, 'Numerical'],
  ['perceptual-speed-accuracy', 4, 'Perceptual Speed'],
  ['spatial-orientation', 7, 'Spatial', { durationMinutes: 5, label: 'Short break' }],
  ['flight-data-memory', 6, 'Working Memory'],
  ['abstract-reasoning', 6, 'Reasoning'],
  ['control-stabilization', 2, 'Psychomotor'],
  ['task-manager', 5, 'Multitasking']
];

const buildModules = () => SECTIONS.map(([moduleId, administrationMinutes, domain, breakAfter]) => {
  const module = MODULE_BY_ID[moduleId];
  if (!module) throw new Error(`Unknown module id "${moduleId}" referenced by the commercial diagnostic profile.`);
  if (!DIAGNOSTIC_DOMAINS.includes(domain)) throw new Error(`Unknown diagnostic domain "${domain}" for module "${moduleId}".`);
  return {
    moduleId,
    name: module.name,
    settingsKey: module.settingsKey,
    administrationMinutes,
    domain,
    breakAfter: breakAfter || null
  };
});

export const COMMERCIAL_DIAGNOSTIC_PROFILE = {
  id: 'commercial-diagnostic-v1',
  version: 1,
  name: 'Commercial Pilot Aptitude Diagnostic',
  shortName: 'Diagnostic',
  description: 'A single standardized run across 7 ability areas, establishing a broad baseline. Not a pass/fail simulation of any specific employer’s test.',
  modules: buildModules()
};

// One diagnostic domain per section in this battery, so a domain score is
// just that section's own academyScore -- no cross-module weighting math.
// Kept as an explicit lookup (rather than reading `domain` off the profile
// module list at every call site) so scoring code has one clear source.
export const DOMAIN_BY_MODULE_ID = Object.fromEntries(
  COMMERCIAL_DIAGNOSTIC_PROFILE.modules.map((item) => [item.moduleId, item.domain])
);

export const getDiagnosticProfileMinutes = (profile = COMMERCIAL_DIAGNOSTIC_PROFILE) => {
  const testMinutes = profile.modules.reduce((sum, item) => sum + item.administrationMinutes, 0);
  const breakMinutes = profile.modules.reduce((sum, item) => sum + (item.breakAfter?.durationMinutes || 0), 0);
  return { testMinutes, breakMinutes, totalMinutes: testMinutes + breakMinutes };
};

// Extends DOMAIN_BY_MODULE_ID's 7 diagnostic-section modules to all 14
// commercial modules, so ongoing practice on a module the diagnostic never
// runs (e.g. Aviation Maths, Mental Rotation) still counts toward that
// module's domain in the live ability profile (utils/commercialLiveScoring.js).
// This is a training-relevance grouping only, independently assigned by
// CBAT Academy -- not a published domain taxonomy from any assessment
// provider. DOMAIN_BY_MODULE_ID itself is left untouched: it stays the
// diagnostic's own 7-module lookup, still used exactly as before by
// commercialDiagnosticScoring.js.
export const ALL_COMMERCIAL_MODULE_DOMAINS = {
  ...DOMAIN_BY_MODULE_ID,
  'aviation-maths': 'Numerical',
  'verbal-reasoning': 'Reasoning',
  'mental-rotation': 'Spatial',
  'physics-for-pilot-selection': 'Reasoning',
  'mechanical-reasoning': 'Reasoning',
  'flight-path-tracking': 'Psychomotor',
  'dynamic-pilot-workload': 'Multitasking'
};
